import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Copy,
  Download,
  Folder,
  FolderOpen,
  MoreVertical,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import * as TeamsModule from 'modules/teams';
import * as CollectionsModule from 'modules/collections';
import type { Collection } from 'modules/collections/types';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { Avatar } from 'components/ui/avatar';
import { LoadMore } from 'components/ui/pagination';
import { toast } from 'components/ui/use-toast';
import { InviteStudentsModal } from 'pages/Teams/InviteStudentsModal';
import { AssignCollectionDialog } from 'pages/Teams/AssignCollectionDialog';
import { downloadStudentsCsv, groupStudents } from 'pages/Teams/groupRoster';
import {
  MobileScreen,
  ScreenHeader,
  HeaderIcon,
  ScreenBody,
  ScrollTabs,
  ListCard,
  BottomSheet,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

type Tab = 'members' | 'collections' | 'materials' | 'settings';

/** Students revealed per "load more"; the group arrives with all of its members. */
const STEP = 10;

/** A full-width secondary button above a tab's list (invite, assign, share). */
function PaneAction({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-3 flex h-10 w-full items-center justify-center gap-2 rounded-md text-[14px] font-medium"
      style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
    >
      {icon}
      {label}
    </button>
  );
}

/** One row of the header's "more" sheet. */
function SheetAction({
  icon,
  label,
  meta,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  meta?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-md p-3 text-left"
      style={{ border: `1px solid ${M.border}` }}
    >
      <span className="flex-shrink-0" style={{ color: M.ink2 }}>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold" style={{ color: M.ink }}>
          {label}
        </span>
        {meta && (
          <span className="block text-[12px]" style={{ color: M.ink3 }}>
            {meta}
          </span>
        )}
      </span>
    </button>
  );
}

/** The group's collections as one card of rows; a row opens the collection. */
function CollectionRows({ collections }: { collections: Collection[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <ListCard>
      {collections.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => navigate(`/app/collections/${c.id}`)}
          className="flex w-full items-center gap-3 px-3.5 py-[13px] text-left"
        >
          <Folder className="h-5 w-5 flex-shrink-0" style={{ color: M.ink2 }} aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-medium leading-[18px]" style={{ color: M.ink }}>
              {c.name}
            </span>
            <span className="mt-0.5 block text-[12.5px] leading-4 tabular-nums" style={{ color: M.ink3 }}>
              {t('collections.items_count', { count: c.itemCount })}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} aria-hidden />
        </button>
      ))}
    </ListCard>
  );
}

/** One full-height screen: students, the group's collections, materials and settings under scrolling tabs. */
export function TeamDetailMobile({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('members');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [shown, setShown] = useState(STEP);

  const { team, isLoading, isError, refetch } = TeamsModule.Hooks.useSingle({ id: teamId, enabled: Boolean(teamId) });
  const {
    collections,
    isLoading: collectionsLoading,
    refetch: refetchCollections,
  } = CollectionsModule.Hooks.useByTeam({ teamId, enabled: Boolean(teamId) });
  const { joinToken } = TeamsModule.Hooks.useJoinToken({ teamId, enabled: Boolean(teamId) });
  const deleteTeam = TeamsModule.Hooks.useDelete();
  const updateTeam = TeamsModule.Hooks.useUpdate();

  const canInvite = usePermission(PERMISSIONS.TEAMS_INVITE);
  const canManage = usePermission(PERMISSIONS.TEAMS_EDIT);

  const students = useMemo(() => (team ? groupStudents(team) : []), [team]);
  // The header's "more" sheet: copy the join code, assign a collection, export the students.
  const hasMoreActions = Boolean(team) && (Boolean(joinToken?.code) || canManage || students.length > 0);

  // Rename, using the same team-update mutation as the desktop Settings page;
  // seeded once from the fetched team, mirroring that page's `seeded` pattern.
  const [settingsName, setSettingsName] = useState('');
  const [settingsSeeded, setSettingsSeeded] = useState(false);
  useEffect(() => {
    if (team && !settingsSeeded) {
      setSettingsName(team.name);
      setSettingsSeeded(true);
    }
  }, [team, settingsSeeded]);
  const settingsDirty = settingsSeeded && Boolean(team) && settingsName !== team?.name;

  const handleSaveSettings = async () => {
    if (!settingsName.trim()) return;
    try {
      await updateTeam.mutateAsync({ id: teamId, name: settingsName.trim() });
    } catch (e: any) {
      toast.error(e?.message || t('error'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('group_danger_confirm'))) return;
    try {
      await deleteTeam.mutateAsync(teamId);
      navigate('/app/teams');
    } catch (e: any) {
      toast.error(e?.message || t('error'));
    }
  };

  const copyCode = (code: string) => {
    setMoreOpen(false);
    navigator.clipboard?.writeText(code).then(
      () => toast.success(t('group_code_copied')),
      () => undefined,
    );
  };

  const tabs = [
    { value: 'members', label: t('group_tab_members'), count: team ? students.length : undefined },
    { value: 'collections', label: t('teams.stat_collections'), count: collectionsLoading ? undefined : collections.length },
    { value: 'materials', label: t('group_tab_materials') },
    ...(canManage ? [{ value: 'settings', label: t('group_tab_settings') }] : []),
  ];

  const renderMembers = () => (
    <>
      {canInvite && (
        <PaneAction icon={<Users className="h-4 w-4" />} label={t('group_invite_students')} onClick={() => setInviteOpen(true)} />
      )}
      {students.length === 0 ? (
        <MobileEmpty icon={<Users size={22} />} title={t('group_no_members')} description={t('group_code_hint')} />
      ) : (
        <>
          <ListCard>
            {students.slice(0, shown).map((m) => (
              <div key={m.userId} className="flex items-center gap-3 px-3.5 py-3">
                <Avatar name={m.user?.name} src={m.user?.avatarUrl} size="sm" />
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium leading-[18px]" style={{ color: M.ink }}>
                  {m.user?.name || t('teams.unknown_user')}
                </span>
                {/* No per-student average from the API yet. */}
                <span className="flex-shrink-0 text-[15px] font-bold tabular-nums" style={{ color: M.faint }}>
                  —
                </span>
              </div>
            ))}
          </ListCard>
          <LoadMore
            compact
            className="mt-3"
            shown={Math.min(shown, students.length)}
            total={students.length}
            step={STEP}
            onLoadMore={() => setShown((n) => n + STEP)}
          />
        </>
      )}
    </>
  );

  const renderCollections = (kind: 'collections' | 'materials') => (
    <>
      {canManage && (
        <PaneAction
          icon={<Plus className="h-4 w-4" />}
          label={kind === 'collections' ? t('group_assign_collection') : t('group_share_material')}
          onClick={() => setAssignOpen(true)}
        />
      )}
      {collectionsLoading ? (
        <MobileSkeleton rows={3} />
      ) : collections.length === 0 ? (
        <MobileEmpty
          icon={<FolderOpen size={22} />}
          title={kind === 'collections' ? t('assigned_collections_empty_title') : t('group_materials_empty_title')}
          description={kind === 'collections' ? t('assigned_collections_empty_desc') : t('group_materials_empty_desc')}
        />
      ) : (
        <CollectionRows collections={collections} />
      )}
    </>
  );

  const renderSettings = () => (
    <>
      <section className="mb-3 rounded-lg p-3.5" style={{ background: M.card, border: `1px solid ${M.border}` }}>
        <h2 className="mb-3 text-[15px] font-semibold" style={{ color: M.ink }}>
          {t('group_settings_details')}
        </h2>
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium" style={{ color: M.ink2 }}>
            {t('group_settings_name')}
          </span>
          <input
            value={settingsName}
            onChange={(e) => setSettingsName(e.target.value)}
            className="h-11 w-full rounded-md px-3 text-[16px] outline-none"
            style={{ border: `1px solid ${M.border}`, color: M.ink }}
          />
        </label>
        {settingsDirty && (
          <button
            type="button"
            onClick={handleSaveSettings}
            disabled={updateTeam.isPending || !settingsName.trim()}
            className="mt-3 flex h-11 w-full items-center justify-center rounded-md text-[15px] font-semibold disabled:opacity-50"
            style={{ background: M.primary, color: M.primaryInk }}
          >
            {t('save_changes')}
          </button>
        )}
      </section>
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteTeam.isPending}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-md text-[14px] font-medium disabled:opacity-50"
        style={{ background: M.card, border: `1px solid ${M.red2}`, color: M.red }}
      >
        <Trash2 className="h-4 w-4" />
        {t('teams.delete_group')}
      </button>
    </>
  );

  const renderBody = () => {
    if (isLoading) return <MobileSkeleton />;
    if (isError) return <MobileError onRetry={() => refetch()} />;
    if (!team) return <MobileEmpty title={t('group_detail_not_found')} />;
    return (
      <ScreenBody>
        {tab === 'members' && renderMembers()}
        {tab === 'collections' && renderCollections('collections')}
        {tab === 'materials' && renderCollections('materials')}
        {tab === 'settings' && canManage && renderSettings()}
      </ScreenBody>
    );
  };

  return (
    <MobileScreen>
      <ScreenHeader
        title={team?.name || t('nav.groups')}
        count={team ? students.length : undefined}
        onBack={() => navigate('/app/teams')}
        right={
          hasMoreActions ? (
            <HeaderIcon icon={<MoreVertical size={19} />} label={t('more_options')} onClick={() => setMoreOpen(true)} />
          ) : undefined
        }
      >
        <ScrollTabs tabs={tabs} value={tab} onChange={(v) => setTab(v as Tab)} aria-label={t('group_tabs')} className="pt-1" />
      </ScreenHeader>

      {renderBody()}

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)}>
        <p className="mb-3.5 truncate text-[15px] font-bold" style={{ color: M.ink }}>
          {team?.name}
        </p>
        <div className="flex flex-col gap-2">
          {joinToken?.code && (
            <SheetAction
              icon={<Copy className="h-[18px] w-[18px]" />}
              label={t('copy_code')}
              meta={<span className="font-mono">{joinToken.code}</span>}
              onClick={() => copyCode(joinToken.code)}
            />
          )}
          {canManage && (
            <SheetAction
              icon={<Plus className="h-[18px] w-[18px]" />}
              label={t('group_assign_collection')}
              onClick={() => {
                setMoreOpen(false);
                setAssignOpen(true);
              }}
            />
          )}
          {team && students.length > 0 && (
            <SheetAction
              icon={<Download className="h-[18px] w-[18px]" />}
              label={t('group_export')}
              onClick={() => {
                setMoreOpen(false);
                downloadStudentsCsv(team, t('teams.unknown_user'));
              }}
            />
          )}
        </div>
      </BottomSheet>

      <InviteStudentsModal open={inviteOpen} onClose={() => setInviteOpen(false)} teamId={teamId} />
      <AssignCollectionDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        teamId={teamId}
        assignedCollectionIds={collections.map((c) => c.id)}
        onAssigned={() => refetchCollections()}
      />
    </MobileScreen>
  );
}

export default TeamDetailMobile;
